// 🔥 Firebase 기반 모델 추가 다이얼로그 - 최고급 구현 (P2 개선)
// [배경] device_catalog + unified_prices 구조에 맞는 새 모델 추가
//        모델 정보 + 구성 옵션 + 초기 가격 설정을 한 번에 처리
// 
// 🔑 P2 핵심 개선:
// 1. Canonical ModelID 미리보기 패널 (입력 즉시 계산)
// 2. 실시간 중복/유사 모델 검사 (canonicalModelId 기준)
// 3. 생성될 PriceId 목록 미리보기
// 4. 저장 전 명시적 확인 체크박스

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  AlertTitle,
  CircularProgress,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DocumentIcon
} from '@mui/icons-material';

import { DeviceCatalogService } from '../../../../services/deviceCatalogService';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { DeviceGrade, GradePriceInfo } from '../../../../types/gradeSystem';
// 🔥 공식 priceId 생성 함수 import
import { generatePriceId, generateModelId } from '../../../../types/deviceCatalog';
import { canonicalizeModelId, buildCanonicalPriceId } from '../../../../utils/idCanonical';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';

// 🔥 wearable 카테고리 지원 추가
interface AddModelDialogProps {
  open: boolean;
  onClose: () => void;
  brand: 'Apple' | 'Samsung';
  deviceType: 'tablet' | 'smartphone' | 'wearable';
  onSuccess: (forceRefresh?: boolean) => void;
  /** 🆕 중복 모델 발견 시 해당 모델의 용량 관리로 이동하는 콜백 */
  onNavigateToVariantManager?: (modelId: string) => void;
}

interface NewModelData {
  modelEn: string;
  modelKo: string;
  series: string;
  storageOptions: string[];
  connectivityOptions: string[];
  colorOptions: string[];
  image: string;
  recommended: boolean;
  releaseYear: number;
  initialPrices: GradePriceInfo;
  setPricesForAllConfigs: boolean;
}

const AddModelDialog: React.FC<AddModelDialogProps> = ({
  open,
  onClose,
  brand,
  deviceType,
  onSuccess,
  onNavigateToVariantManager
}) => {
  // 🆕 중복 발견된 모델 ID 저장 (해당 모델로 이동 기능용)
  const [duplicateModelId, setDuplicateModelId] = React.useState<string | null>(null);
  
  // 🔑 P2 개선: Canonical ID 미리보기 및 중복 검사 상태
  const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);
  const [duplicateStatus, setDuplicateStatus] = useState<{
    type: 'ok' | 'exact' | 'similar' | 'checking';
    message: string;
    existingModelId?: string;
    similarModels?: Array<{ id: string; name: string }>;
  }>({ type: 'checking', message: '' });
  const [priceIdPreviewExpanded, setPriceIdPreviewExpanded] = useState(true);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  
  // 📝 폼 상태 - deviceType에 따라 초기값 설정
  const getInitialFormData = (): NewModelData => ({
    modelEn: '',
    modelKo: '',
    series: '',
    // 🎧 웨어러블은 구성 옵션(풀세트/유닛세트 등)을 storage로 사용
    storageOptions: deviceType === 'wearable' ? ['풀세트', '유닛 세트', '유닛 1쪽', '본체만'] : ['128GB'],
    // 📱 스마트폰은 연결성 옵션이 '기본'으로 고정 (셀룰러 기본 내장)
    connectivityOptions: (deviceType === 'wearable' || deviceType === 'smartphone') ? ['기본'] : ['Wi-Fi'],
    colorOptions: [],
    image: '',
    recommended: false,
    releaseYear: new Date().getFullYear(),
    initialPrices: { A: 0, B: 0, C: 0, D: 0, E: 0 },
    setPricesForAllConfigs: true
  });
  
  const [formData, setFormData] = useState<NewModelData>(getInitialFormData());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 🔄 deviceType/brand 변경 시 formData 재설정 (다이얼로그 재사용 대응)
  React.useEffect(() => {
    setFormData(getInitialFormData());
  }, [deviceType, brand]);

  // 🎯 시리즈 옵션 (브랜드별 + wearable 지원)
  // 📱 Apple 스마트폰: 대표 시리즈만 표시 (세부 모델은 모델명에서 구분)
  const seriesOptions = deviceType === 'tablet' ? (
    brand === 'Apple' ? 
      ['iPad Pro', 'iPad Air', 'iPad mini', 'iPad'] :
      ['Galaxy Tab S', 'Galaxy Tab A', 'Galaxy Tab Active', 'Galaxy Tab Pro']
  ) : deviceType === 'wearable' ? (
    // 🎧 웨어러블 시리즈 옵션
    brand === 'Apple' ?
      ['AirPods', 'AirPods Pro', 'AirPods Max'] :
      ['Galaxy Buds', 'Galaxy Buds Pro', 'Galaxy Watch']
  ) : (
    brand === 'Apple' ?
      [
        // 📱 대표 시리즈 (6~17, 최신순) - 신규 시리즈 추가 시 여기에 추가
        'iPhone 17', 'iPhone 16', 'iPhone 15', 'iPhone 14', 'iPhone 13', 'iPhone 12', 'iPhone 11',
        'iPhone 8', 'iPhone 7', 'iPhone 6',
        // 📱 특별 시리즈
        'iPhone XS', 'iPhone XR', 'iPhone X', 'iPhone SE'
      ] :
      ['Galaxy S', 'Galaxy Z Flip', 'Galaxy Z Fold', 'Galaxy Note', 'Galaxy A']
  );

  // 🎯 기본 스토리지 옵션 (wearable은 연결 타입 사용)
  // 📱 스마트폰: 32GB, 64GB 추가 (저용량 구형 모델 지원)
  const defaultStorageOptions = deviceType === 'tablet' ? 
    ['64GB', '128GB', '256GB', '512GB', '1TB', '2TB'] :
    deviceType === 'wearable' ?
    ['USB-C', 'Lightning', 'MagSafe', '유선 충전', '무선 충전', '기본'] :
    ['32GB', '64GB', '128GB', '256GB', '512GB', '1TB'];

  // 🎯 연결성 옵션 (wearable은 충전 타입 사용)
  const defaultConnectivityOptions = deviceType === 'tablet' ?
    ['Wi-Fi', 'Wi-Fi + Cellular', 'Wi-Fi + 5G'] :
    deviceType === 'wearable' ?
    ['USB-C', 'Lightning', 'MagSafe', '유선 충전', '무선 충전', '기본'] :
    ['기본'];

  // 🔧 스토리지/연결성 옵션 추가/제거
  const addStorageOption = () => {
    setFormData(prev => ({
      ...prev,
      storageOptions: [...prev.storageOptions, '256GB']
    }));
  };

  const removeStorageOption = (index: number) => {
    setFormData(prev => ({
      ...prev,
      storageOptions: prev.storageOptions.filter((_, i) => i !== index)
    }));
  };

  const addConnectivityOption = () => {
    setFormData(prev => ({
      ...prev,
      connectivityOptions: [...prev.connectivityOptions, 'Wi-Fi + Cellular']
    }));
  };

  const removeConnectivityOption = (index: number) => {
    setFormData(prev => ({
      ...prev,
      connectivityOptions: prev.connectivityOptions.filter((_, i) => i !== index)
    }));
  };

  // 🔑 P2 핵심: Canonical Model ID 계산 (입력값 기반 실시간 계산)
  const previewCanonicalModelId = useMemo(() => {
    if (!formData.modelEn.trim() || !formData.series.trim()) {
      return '';
    }
    
    const modelId = generateModelId({
      brand,
      deviceType,
      series: formData.series,
      modelEn: formData.modelEn
    });
    
    // canonicalizeModelId로 최종 정규화 (+ 기호 보존 등)
    return canonicalizeModelId(modelId);
  }, [formData.modelEn, formData.series, brand, deviceType]);

  // 🔑 P2 핵심: 생성될 Price ID 목록 미리보기
  const previewPriceIds = useMemo(() => {
    if (!previewCanonicalModelId) return [];
    
    const priceIds: Array<{ id: string; storage: string; connectivity: string }> = [];
    
    formData.storageOptions.forEach(storage => {
      formData.connectivityOptions.forEach(connectivity => {
        const effectiveStorage = storage;
        const effectiveConnectivity = (deviceType === 'wearable' || connectivity === 'Wi-Fi' || connectivity === '기본') 
          ? undefined 
          : connectivity;
        
        try {
          const priceId = buildCanonicalPriceId(
            previewCanonicalModelId,
            deviceType,
            { storage: effectiveStorage, connectivity: effectiveConnectivity },
            'kr'
          );
          priceIds.push({ id: priceId, storage, connectivity });
        } catch (e) {
          // 무시
        }
      });
    });
    
    return priceIds;
  }, [previewCanonicalModelId, formData.storageOptions, formData.connectivityOptions, deviceType]);

  // 🔑 P2 핵심: Canonical Model ID 기준 중복 검사 (실시간)
  useEffect(() => {
    if (!previewCanonicalModelId) {
      setDuplicateStatus({ type: 'checking', message: '모델명을 입력하세요' });
      return;
    }

    const checkCanonicalDuplicate = async () => {
      setDuplicateCheckLoading(true);
      setDuplicateStatus({ type: 'checking', message: '중복 검사 중...' });
      
      try {
        // 🔑 P2 수정: Exact Match 검사를 3단계로 확장
        // 1-1. Canonical Model ID로 직접 문서 조회
        const docRef = doc(db, 'device_catalog', previewCanonicalModelId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const existingModel = docSnap.data();
          setDuplicateStatus({
            type: 'exact',
            message: `이미 존재하는 모델 ID입니다: "${existingModel.modelKo || existingModel.modelEn}"`,
            existingModelId: previewCanonicalModelId
          });
          setDuplicateModelId(previewCanonicalModelId);
          setDuplicateCheckLoading(false);
          return;
        }
        
        // 1-2. 🔑 modelKo + series 기준 Exact Match (레거시 ID 문서 대응)
        // [배경] 기존 DB에 레거시 형식 ID로 저장된 문서가 있으면
        //        Canonical ID로 조회해도 찾지 못함 → modelKo로 추가 검사
        if (formData.modelKo.trim()) {
          const exactMatchQuery = query(
            collection(db, 'device_catalog'),
            where('brand', '==', brand),
            where('deviceType', '==', deviceType),
            where('series', '==', formData.series),
            where('modelKo', '==', formData.modelKo.trim())
          );
          const exactMatchSnapshot = await getDocs(exactMatchQuery);
          
          if (!exactMatchSnapshot.empty) {
            const existingDoc = exactMatchSnapshot.docs[0];
            const existingModel = existingDoc.data();
            setDuplicateStatus({
              type: 'exact',
              message: `이미 존재하는 모델입니다: "${existingModel.modelKo}" (${existingDoc.id})`,
              existingModelId: existingDoc.id
            });
            setDuplicateModelId(existingDoc.id);
            setDuplicateCheckLoading(false);
            return;
          }
        }
        
        // 1-3. 🔑 modelEn 기준 Exact Match (영문명 중복 검사)
        if (formData.modelEn.trim()) {
          const exactEnMatchQuery = query(
            collection(db, 'device_catalog'),
            where('brand', '==', brand),
            where('deviceType', '==', deviceType),
            where('series', '==', formData.series),
            where('modelEn', '==', formData.modelEn.trim())
          );
          const exactEnMatchSnapshot = await getDocs(exactEnMatchQuery);
          
          if (!exactEnMatchSnapshot.empty) {
            const existingDoc = exactEnMatchSnapshot.docs[0];
            const existingModel = existingDoc.data();
            setDuplicateStatus({
              type: 'exact',
              message: `이미 존재하는 모델입니다: "${existingModel.modelEn}" (${existingDoc.id})`,
              existingModelId: existingDoc.id
            });
            setDuplicateModelId(existingDoc.id);
            setDuplicateCheckLoading(false);
            return;
          }
        }
        
        // 2. Similar Match: 동일 시리즈 내 유사 모델 검색
        const q = query(
          collection(db, 'device_catalog'),
          where('brand', '==', brand),
          where('deviceType', '==', deviceType),
          where('series', '==', formData.series)
        );
        const snapshot = await getDocs(q);
        
        const similarModels: Array<{ id: string; name: string }> = [];
        
        // 모델명에서 핵심 키워드 추출 (숫자 + plus 변형)
        const extractModelCore = (name: string) => {
          return name.toLowerCase()
            .replace(/[^a-z0-9+]/g, '')
            .replace(/plus/gi, '+');
        };
        
        const inputCore = extractModelCore(formData.modelEn);
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const existingCore = extractModelCore(data.modelEn || '');
          
          // 핵심 키워드가 유사한 경우 (S25, S25+, S25++ 등)
          if (inputCore !== existingCore && 
              (inputCore.includes(existingCore) || existingCore.includes(inputCore))) {
            similarModels.push({
              id: doc.id,
              name: data.modelKo || data.modelEn
            });
          }
        });
        
        if (similarModels.length > 0) {
          setDuplicateStatus({
            type: 'similar',
            message: `동일 시리즈 내 유사 모델이 존재합니다`,
            similarModels
          });
        } else {
          setDuplicateStatus({
            type: 'ok',
            message: '생성 가능 (중복 없음)'
          });
        }
        
        setDuplicateModelId(null);
      } catch (error) {
        console.error('중복 검사 오류:', error);
        setDuplicateStatus({
          type: 'ok',
          message: '중복 검사 완료'
        });
      } finally {
        setDuplicateCheckLoading(false);
      }
    };
    
    // 디바운스: 300ms 후 검사
    const timer = setTimeout(checkCanonicalDuplicate, 300);
    return () => clearTimeout(timer);
  }, [previewCanonicalModelId, brand, deviceType, formData.series, formData.modelKo, formData.modelEn]);

  // 기존 중복 검사 함수 (modelKo + series 기준) - 호환성 유지
  const checkDuplicate = async (modelKo: string, series: string): Promise<{ isDuplicate: boolean; modelId?: string }> => {
    const q = query(
      collection(db, 'device_catalog'),
      where('brand', '==', brand),
      where('deviceType', '==', deviceType),
      where('modelKo', '==', modelKo),
      where('series', '==', series)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return { isDuplicate: true, modelId: snapshot.docs[0].id };
    }
    return { isDuplicate: false };
  };

  // ✅ 폼 유효성 검사
  const validateForm = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!formData.modelEn.trim()) errors.push('영문 모델명이 필요합니다.');
    if (!formData.modelKo.trim()) errors.push('한글 모델명이 필요합니다.');
    if (!formData.series.trim()) errors.push('시리즈 선택이 필요합니다.');
    if (formData.storageOptions.length === 0) errors.push('최소 1개의 스토리지 옵션이 필요합니다.');
    if (formData.connectivityOptions.length === 0) errors.push('최소 1개의 연결성 옵션이 필요합니다.');

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  // 🔥 모델 추가 실행
  const handleAddModel = async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      return;
    }

    try {
      setLoading(true);

      // 🔥 중복 검사 로직 추가 (개선된 안내 메시지 + 해당 모델로 이동 기능)
      const duplicateCheck = await checkDuplicate(formData.modelKo, formData.series);
      
      if (duplicateCheck.isDuplicate) {
        setDuplicateModelId(duplicateCheck.modelId || null);
        setError(
          `⚠️ 이미 존재하는 모델입니다: [${formData.series}] ${formData.modelKo}\n\n` +
          `💡 새 용량(예: 512GB)을 추가하려면:\n` +
          `   "해당 모델로 이동" 버튼을 클릭하거나,\n` +
          `   모델 카드의 💾 용량 관리 버튼을 사용하세요.\n\n` +
          `이 다이얼로그는 완전히 새로운 모델을 등록할 때만 사용됩니다.`
        );
        setLoading(false);
        return;
      }
      
      // 중복이 아니면 duplicateModelId 초기화
      setDuplicateModelId(null);

      setError(null);

      console.log('🔥 Firebase 모델 추가 시작:', formData.modelKo);

      // 1단계: device_catalog에 모델 추가
      const modelId = await DeviceCatalogService.addNewModel({
        deviceType: deviceType,
        brand,
        series: formData.series,
        modelEn: formData.modelEn,
        modelKo: formData.modelKo,
        storageOptions: formData.storageOptions,
        connectivityOptions: formData.connectivityOptions,
        // 🎧 웨어러블 기본 이미지: AirPods 3세대
        // 📸 스마트폰은 빈 값 저장 → 사용자 UI의 phoneImageResolver가 모델명으로 실존 이미지 자동 매칭
        //    (기존 '/assets/default-smartphone.png'는 존재하지 않는 파일이라 카탈로그를 오염시켰음)
        image: formData.image || (deviceType === 'wearable'
          ? '/assets/airPot/에어팟3세대.png'
          : deviceType === 'smartphone' ? '' : `/assets/default-${deviceType}.png`),
        recommended: formData.recommended,
        releaseYear: formData.releaseYear
      }, 'admin');

      console.log(`✅ device_catalog 저장 완료: ${modelId}`);

      // 2단계: 모든 구성에 대해 초기 가격 설정 (옵션)
      if (formData.setPricesForAllConfigs && Object.values(formData.initialPrices).some(p => p > 0)) {
        console.log('💰 모든 구성에 초기 가격 설정...');

        const UnifiedPriceService = getUnifiedPriceService();
        const pricePromises: Promise<any>[] = [];

        formData.storageOptions.forEach(storage => {
          formData.connectivityOptions.forEach(connectivity => {
            // 🔥 공식 generatePriceId 함수 사용 (일관성 보장)
            // 🎧 웨어러블은 항상 storage='기본', connectivity=undefined로 priceId 생성
            const effectiveStorage = storage;
            const effectiveConnectivity = (deviceType === 'wearable' || connectivity === 'Wi-Fi' || connectivity === '기본') 
              ? undefined 
              : connectivity;
            
            const priceId = generatePriceId(
              modelId,
              { 
                storage: effectiveStorage, 
                connectivity: effectiveConnectivity 
              },
              'KR'
            );

            console.log(`💰 초기 가격 생성: ${priceId}`);
            console.log(`  모델ID: ${modelId}`);
            console.log(`  구성: ${storage} ${connectivity}`);
            console.log(`  가격: A=${formData.initialPrices.A} B=${formData.initialPrices.B} C=${formData.initialPrices.C} D=${formData.initialPrices.D}`);

            pricePromises.push(
              UnifiedPriceService.updatePrice({
                priceId,
                gradePrices: formData.initialPrices,
                memo: `신규 모델 추가 시 초기 가격 - ${new Date().toLocaleString()}`
              }, 'admin')
            );
          });
        });

        await Promise.all(pricePromises);
        console.log(`✅ ${pricePromises.length}개 구성의 초기 가격 설정 완료`);
      }

      // 성공 처리
      console.log('🎉 모델 추가 완료');
      onSuccess(true); // 강제 새로고침 요청
      handleClose();

    } catch (error) {
      console.error('❌ 모델 추가 실패:', error);
      setError(`모델 추가 실패: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // 🔄 다이얼로그 초기화 - deviceType에 따라 적절한 초기값 사용
  const handleClose = () => {
    setFormData(getInitialFormData());
    setError(null);
    setLoading(false);
    setDuplicateModelId(null);
    setConfirmationChecked(false); // 🔑 P2: 확인 체크박스 초기화
    setDuplicateStatus({ type: 'checking', message: '' }); // 🔑 P2: 중복 상태 초기화
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon color="primary" />
          새 {brand} {deviceType === 'tablet' ? '태블릿' : deviceType === 'smartphone' ? '스마트폰' : '웨어러블'} 모델 추가
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* 🔑 P2 핵심: Canonical Model ID 미리보기 패널 */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: duplicateStatus.type === 'exact' ? '#fef2f2' : 
                     duplicateStatus.type === 'similar' ? '#fffbeb' : 
                     duplicateStatus.type === 'ok' ? '#f0fdf4' : '#f3f4f6',
            border: '2px solid',
            borderColor: duplicateStatus.type === 'exact' ? '#ef4444' : 
                         duplicateStatus.type === 'similar' ? '#f59e0b' : 
                         duplicateStatus.type === 'ok' ? '#22c55e' : '#d1d5db'
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📍 생성될 Model ID (Canonical)
            {duplicateCheckLoading && <CircularProgress size={16} />}
          </Typography>
          
          <Box sx={{ 
            bgcolor: '#fff', 
            p: 1.5, 
            borderRadius: 1, 
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5
          }}>
            <Typography component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {previewCanonicalModelId || '(모델명 입력 필요)'}
            </Typography>
            {previewCanonicalModelId && (
              <Tooltip title="ID 복사">
                <IconButton 
                  size="small" 
                  onClick={() => navigator.clipboard.writeText(previewCanonicalModelId)}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {/* 중복 상태 표시 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {duplicateStatus.type === 'ok' && (
              <>
                <CheckCircleIcon sx={{ color: '#22c55e' }} fontSize="small" />
                <Typography variant="body2" color="success.main" fontWeight={600}>
                  🟢 {duplicateStatus.message}
                </Typography>
              </>
            )}
            {duplicateStatus.type === 'exact' && (
              <>
                <ErrorIcon sx={{ color: '#ef4444' }} fontSize="small" />
                <Typography variant="body2" color="error" fontWeight={600}>
                  🔴 {duplicateStatus.message}
                </Typography>
              </>
            )}
            {duplicateStatus.type === 'similar' && (
              <>
                <WarningIcon sx={{ color: '#f59e0b' }} fontSize="small" />
                <Typography variant="body2" color="warning.main" fontWeight={600}>
                  🟡 {duplicateStatus.message}
                </Typography>
              </>
            )}
            {duplicateStatus.type === 'checking' && (
              <Typography variant="body2" color="text.secondary">
                {duplicateStatus.message}
              </Typography>
            )}
          </Box>
          
          {/* 유사 모델 목록 */}
          {duplicateStatus.similarModels && duplicateStatus.similarModels.length > 0 && (
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fffbeb', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                유사 모델:
              </Typography>
              {duplicateStatus.similarModels.map((m, idx) => (
                <Chip
                  key={idx}
                  label={`${m.name} (${m.id})`}
                  size="small"
                  variant="outlined"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
          )}
        </Paper>

        {error && (
          <Alert 
            severity={error.includes('이미 존재하는 모델') ? 'warning' : 'error'} 
            sx={{ 
              mb: 3,
              '& .MuiAlert-message': { 
                whiteSpace: 'pre-line',
                width: '100%'
              }
            }}
            icon={error.includes('이미 존재하는 모델') ? <WarningIcon /> : undefined}
          >
            {error.includes('이미 존재하는 모델') ? (
              <Box>
                <AlertTitle sx={{ fontWeight: 700, color: '#b45309' }}>
                  이미 존재하는 모델입니다
                </AlertTitle>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>[{formData.series}] {formData.modelKo}</strong> 모델은 이미 등록되어 있습니다.
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbeb', border: '1px solid #f59e0b' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom>
                    💡 새 용량(예: 512GB)을 추가하려면:
                  </Typography>
                  {/* 🆕 해당 모델로 바로 이동하는 버튼 */}
                  {duplicateModelId && onNavigateToVariantManager && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mb: 2 }}
                      onClick={() => {
                        onNavigateToVariantManager(duplicateModelId);
                        handleClose();
                      }}
                      startIcon={<SettingsIcon />}
                    >
                      🔗 해당 모델로 이동하여 용량 추가하기
                    </Button>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    또는 다이얼로그를 닫고 모델 카드의 <strong>💾 용량 관리</strong> 버튼을 사용하세요.
                  </Typography>
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  ※ 이 다이얼로그는 완전히 새로운 모델을 등록할 때만 사용됩니다.
                </Typography>
              </Box>
            ) : (
              error
            )}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* 기본 정보 */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>📱 기본 정보</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="영문 모델명"
              placeholder="iPad Air 6th M2"
              value={formData.modelEn}
              onChange={(e) => setFormData(prev => ({ ...prev, modelEn: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="한글 모델명"
              placeholder="아이패드 에어 6세대 M2"
              value={formData.modelKo}
              onChange={(e) => setFormData(prev => ({ ...prev, modelKo: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>시리즈</InputLabel>
              <Select
                value={formData.series}
                onChange={(e) => setFormData(prev => ({ ...prev, series: e.target.value }))}
              >
                {seriesOptions.map(series => (
                  <MenuItem key={series} value={series}>{series}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="출시년도"
              value={formData.releaseYear}
              onChange={(e) => setFormData(prev => ({ ...prev, releaseYear: parseInt(e.target.value) || new Date().getFullYear() }))}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="이미지 경로"
              placeholder="/assets/ipad/new-model.png"
              value={formData.image}
              onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.recommended}
                  onChange={(e) => setFormData(prev => ({ ...prev, recommended: e.target.checked }))}
                />
              }
              label="추천 모델"
            />
          </Grid>

          {/* 구성 옵션 */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>⚙️ 구성 옵션</Typography>
          </Grid>

          {/* 스토리지 옵션 */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>스토리지 옵션</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addStorageOption}
                size="small"
                variant="outlined"
              >
                추가
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {formData.storageOptions.map((storage, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={storage}
                      onChange={(e) => {
                        const newOptions = [...formData.storageOptions];
                        newOptions[index] = e.target.value;
                        setFormData(prev => ({ ...prev, storageOptions: newOptions }));
                      }}
                    >
                      {defaultStorageOptions.map(option => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.storageOptions.length > 1 && (
                    <IconButton size="small" onClick={() => removeStorageOption(index)} color="error">
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>

          {/* 연결성 옵션 */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>연결성 옵션</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addConnectivityOption}
                size="small"
                variant="outlined"
              >
                추가
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {formData.connectivityOptions.map((connectivity, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                      value={connectivity}
                      onChange={(e) => {
                        const newOptions = [...formData.connectivityOptions];
                        newOptions[index] = e.target.value;
                        setFormData(prev => ({ ...prev, connectivityOptions: newOptions }));
                      }}
                    >
                      {defaultConnectivityOptions.map(option => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.connectivityOptions.length > 1 && (
                    <IconButton size="small" onClick={() => removeConnectivityOption(index)} color="error">
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>

          {/* 초기 가격 설정 */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">💰 초기 가격 설정</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.setPricesForAllConfigs}
                    onChange={(e) => setFormData(prev => ({ ...prev, setPricesForAllConfigs: e.target.checked }))}
                  />
                }
                label="모든 구성에 적용"
              />
            </Box>
          </Grid>

          {formData.setPricesForAllConfigs && (
            <>
              {(['A', 'B', 'C', 'D'] as DeviceGrade[]).map(grade => (
                <Grid item xs={12} sm={6} md={3} key={grade}>
                  <TextField
                    fullWidth
                    label={`${grade}등급 가격`}
                    type="number"
                    value={formData.initialPrices[grade]}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      setFormData(prev => ({
                        ...prev,
                        initialPrices: { ...prev.initialPrices, [grade]: value }
                      }));
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">원</InputAdornment>
                    }}
                  />
                </Grid>
              ))}
            </>
          )}

          {/* 🔑 P2 개선: 생성될 가격 문서 미리보기 (priceId 포함) */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                mb: 1
              }}
              onClick={() => setPriceIdPreviewExpanded(!priceIdPreviewExpanded)}
            >
              <Typography variant="h6">
                📋 생성될 가격 문서 ({previewPriceIds.length}개)
              </Typography>
              <IconButton size="small">
                {priceIdPreviewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            
            <Collapse in={priceIdPreviewExpanded}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  maxHeight: '300px', 
                  overflow: 'auto',
                  bgcolor: previewPriceIds.length > 0 ? '#f8fafc' : '#fff'
                }}
              >
                {previewPriceIds.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    모델명과 구성을 입력하면 생성될 가격 문서가 표시됩니다.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {previewPriceIds.map((price, idx) => (
                      <ListItem 
                        key={idx} 
                        sx={{ 
                          py: 0.5,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          borderBottom: idx < previewPriceIds.length - 1 ? '1px solid #e5e7eb' : 'none'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <DocumentIcon fontSize="small" sx={{ color: '#6b7280' }} />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: '0.8rem',
                              wordBreak: 'break-all',
                              flex: 1
                            }}
                          >
                            {price.id}
                          </Typography>
                          <Chip 
                            label="신규" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                          구성: {price.storage} / {price.connectivity} / KR
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Collapse>
          </Grid>
        </Grid>

        {/* 안내 정보 */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>📋 생성 과정:</strong><br/>
            1. device_catalog 컬렉션에 모델 정보 저장<br/>
            2. 모든 스토리지×연결성 조합에 대해 unified_prices 문서 생성<br/>
            3. 관리자 페이지 자동 새로고침으로 즉시 표시
          </Typography>
        </Alert>

        {/* 기존 모델에 용량 추가 안내 */}
        <Alert severity="success" sx={{ mt: 2 }} icon={<InfoIcon />}>
          <Typography variant="body2">
            <strong>💡 기존 모델에 새 용량 추가하기:</strong><br/>
            이미 등록된 모델에 새 용량(예: 512GB)을 추가하려면,<br/>
            테이블에서 해당 모델의 <SettingsIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> 버튼 → <strong>"💾 용량(Variant) 관리"</strong> 사용
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
        {/* 🔑 P2 핵심: 최종 확인 체크박스 */}
        {duplicateStatus.type !== 'exact' && previewCanonicalModelId && (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 1.5, 
              bgcolor: confirmationChecked ? '#f0fdf4' : '#fefce8',
              border: '1px solid',
              borderColor: confirmationChecked ? '#22c55e' : '#facc15'
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  color="success"
                />
              }
              label={
                <Typography variant="body2">
                  위 Model ID(<strong>{previewCanonicalModelId}</strong>)로 생성되는 것을 확인했습니다.
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Paper>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={handleClose} disabled={loading}>
            취소
          </Button>
          <Button 
            onClick={handleAddModel}
            variant="contained"
            disabled={
              loading || 
              !validateForm().isValid || 
              duplicateStatus.type === 'exact' ||
              (previewCanonicalModelId && !confirmationChecked)
            }
            startIcon={loading ? undefined : <SaveIcon />}
            color={duplicateStatus.type === 'similar' ? 'warning' : 'primary'}
          >
            {loading ? '생성 중...' : 
             duplicateStatus.type === 'exact' ? '생성 불가 (중복)' :
             duplicateStatus.type === 'similar' ? '확인 후 생성' :
             '모델 추가'}
          </Button>
        </Box>
        
        {/* 저장 불가 사유 표시 */}
        {duplicateStatus.type === 'exact' && (
          <Alert severity="error" sx={{ width: '100%' }}>
            동일한 Model ID가 이미 존재합니다. 기존 모델의 용량 관리 기능을 사용하세요.
          </Alert>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddModelDialog;
